import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

/** Guest auth endpoints where a 401 means "bad credentials/token", NOT a
 *  session that expired — the page that called them owns the error. */
const AUTH_ENDPOINT = /\/auth\/(login|forgot-password|reset-password)$/;

/**
 * Centralized error handling for all API responses.
 *  - 401 → clear session and redirect to login (EXCEPT on the guest auth
 *    endpoints, where a 401 is a credential error the calling page handles)
 *  - 419 → Sanctum session/CSRF expired (stateful SPA) → clear session and
 *    redirect to login with a "session expired" notice
 *  - 403 → show "no permission" toast
 *  - 422 → let component handle validation errors
 *  - 5xx → show generic error toast
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      switch (err.status) {
        case 401:
          // A 401 from login/forgot/reset is "wrong credentials" — let the login
          // page show its error. Hijacking it here (clearSession + navigate) both
          // duplicates the redirect and pollutes returnUrl, which then makes the
          // NEXT successful login fail to navigate. Only treat 401s from
          // authenticated endpoints as a session-expiry.
          if (!AUTH_ENDPOINT.test(req.url)) {
            // Clear *all* auth state (token + persisted user + signal), not just
            // the token, then redirect preserving the attempted URL.
            auth.clearSession(false);
            router.navigate(['/authentication'], { queryParams: { returnUrl: router.url } });
          }
          break;
        case 419:
          // Sanctum stateful CSRF/session expired (fires before auth, so no user).
          // Treat as a session expiry: clear state and send back to login.
          auth.clearSession(false);
          snackBar.open('Your session expired. Please sign in again.', 'Close', { duration: 5000 });
          router.navigate(['/authentication'], { queryParams: { returnUrl: router.url } });
          break;
        case 403:
          snackBar.open('You do not have permission to perform this action.', 'Close', { duration: 5000 });
          break;
        case 0:
        case 500:
        case 502:
        case 503:
        case 504:
          snackBar.open('A server error occurred. Please try again later.', 'Close', { duration: 5000 });
          break;
      }
      return throwError(() => err);
    })
  );
};
